import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Circle, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface GoDecisionItem {
  label: string;
  checked: boolean;
  description?: string;
  reference?: string;
}

interface GoDecisionBlockProps {
  data?: {
    items?: GoDecisionItem[];
    overall_ready?: boolean;
  };
  recommendedOption?: string;
  validationDays?: number;
}

export function GoDecisionBlock({ data, recommendedOption, validationDays }: GoDecisionBlockProps) {
  const items = data?.items;
  const hasItems = items && items.length > 0;
  const allChecked = hasItems ? items.every(item => item.checked) : false;
  const overallReady = data?.overall_ready ?? allChecked;

  return (
    <Card 
      id="go-decision"
      className={cn(
        "border-2 scroll-mt-24",
        !hasItems
          ? "border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent"
          : overallReady 
            ? "border-green-500/50 bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent" 
            : "border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent"
      )}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            {hasItems && overallReady ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <Circle className="h-5 w-5 text-amber-500" />
            )}
            Go-Decision Check
          </span>
          {hasItems ? (
            overallReady ? (
              <span className="text-sm font-normal text-green-600 dark:text-green-400">
                ✓ Bereit zur Umsetzung
              </span>
            ) : (
              <span className="text-sm font-normal text-amber-600 dark:text-amber-400">
                ⚠ Offene Punkte
              </span>
            )
          ) : (
            <span className="text-sm font-normal text-amber-600 dark:text-amber-400">
              ⚠ Manuelle Prüfung erforderlich
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasItems ? (
          <div className="space-y-3">
            {items.map((item, index) => (
              <div 
                key={index}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg transition-colors",
                  item.checked ? "bg-green-500/10" : "bg-amber-500/10"
                )}
              >
                {item.checked ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                ) : (
                  <Circle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={cn(
                    "font-medium text-sm",
                    item.checked ? "text-green-700 dark:text-green-300" : "text-amber-700 dark:text-amber-300"
                  )}>
                    {item.label}
                  </p>
                  {item.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                  )}
                  {item.reference && (
                    <p className="text-xs text-primary/70 mt-1 italic">Siehe: {item.reference}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm text-amber-700 dark:text-amber-300">
                Go-Decision Check wurde nicht automatisch generiert.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Bitte prüfen Sie die Entscheidungsreife manuell anhand der Report-Sektionen.
              </p>
            </div>
          </div>
        )}

        {/* Final Statement */}
        {recommendedOption && hasItems && overallReady && (
          <div className="mt-4 pt-4 border-t border-green-500/20">
            <p className="text-sm text-center font-medium">
              <span className="text-green-600 dark:text-green-400">Empfehlung:</span>{" "}
              <strong>{recommendedOption}</strong> umsetzen und in {validationDays || 90} Tagen evaluieren.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
